/**
 * Client-side throttle that stops a single runaway error from exhausting the
 * Sentry quota.
 *
 * We blew through our 50k monthly error cap when one client left a tab open and
 * the same exception was reported every ~10 seconds for days. Sentry's default
 * `dedupeIntegration` only drops events that are *immediately consecutive* and
 * identical, so anything interleaved (or with a slightly varying message) slips
 * through and keeps billing. This throttle is coarser and more durable: once a
 * given error fingerprint has been reported `maxEventsPerWindow` times inside
 * `windowMs`, further matching events are dropped until the window rolls over.
 * Distinct errors hash to different fingerprints, so they are unaffected.
 *
 * It is deliberately SDK-agnostic (no `@sentry/*` import) so the same instance
 * can be shared between the React and AWS-serverless SDKs, which expose the same
 * `ErrorEvent` shape. The throttle is in-memory and per-process: on the frontend
 * it lives for the page session (which is exactly the runaway-tab case), and on
 * the backend it lives for the warm Lambda container. For protection that spans
 * page reloads / cold starts, pair this with Sentry's project-level spike
 * protection and per-key rate limits in the dashboard.
 */

/** Minimal structural subset of Sentry's `ErrorEvent` that we fingerprint on. */
export interface ThrottleableSentryEvent {
   message?: string
   exception?: {
      values?: Array<{
         type?: string
         value?: string
         stacktrace?: {
            frames?: Array<{
               filename?: string
               function?: string
            }>
         }
      }>
   }
}

export interface SentryThrottleOptions {
   /** Length of the rolling window in milliseconds. Defaults to 60s. */
   windowMs?: number
   /** Max events per fingerprint allowed within the window. Defaults to 5. */
   maxEventsPerWindow?: number
   /**
    * Safety cap on how many distinct fingerprints we track at once. When
    * exceeded the map is cleared, so a long session that produces thousands of
    * unique errors can't leak memory. Defaults to 1000.
    */
   maxFingerprints?: number
   /** Injectable clock for testing. Defaults to `Date.now`. */
   now?: () => number
}

const DEFAULT_WINDOW_MS = 60_000
const DEFAULT_MAX_EVENTS_PER_WINDOW = 5
const DEFAULT_MAX_FINGERPRINTS = 1000

/**
 * Builds a coarse fingerprint for an event from its exception type, message and
 * top stack frame. Two reports of the same bug collapse to the same key; two
 * different bugs do not.
 */
export function fingerprintSentryEvent(event: ThrottleableSentryEvent): string {
   const exceptionValues = event.exception?.values
   const exception = exceptionValues?.[exceptionValues.length - 1]
   const type = exception?.type ?? event.message ?? 'unknown'
   const value = exception?.value ?? ''
   const frames = exception?.stacktrace?.frames
   const topFrame = frames?.[frames.length - 1]
   const location = topFrame ? `${topFrame.filename ?? ''}:${topFrame.function ?? ''}` : ''
   return `${type}|${value}|${location}`
}

/**
 * Returns a predicate for use inside a Sentry `beforeSend` hook: `true` means
 * send the event, `false` means drop it (the hook should then return `null`).
 */
export function createSentryEventThrottle(options: SentryThrottleOptions = {}) {
   const windowMs = options.windowMs ?? DEFAULT_WINDOW_MS
   const maxEventsPerWindow = options.maxEventsPerWindow ?? DEFAULT_MAX_EVENTS_PER_WINDOW
   const maxFingerprints = options.maxFingerprints ?? DEFAULT_MAX_FINGERPRINTS
   const now = options.now ?? (() => Date.now())

   const timestampsByFingerprint = new Map<string, number[]>()

   return function shouldSendSentryEvent(event: ThrottleableSentryEvent): boolean {
      const fingerprint = fingerprintSentryEvent(event)
      const currentTime = now()
      const windowStart = currentTime - windowMs

      const recentTimestamps = (timestampsByFingerprint.get(fingerprint) ?? []).filter(timestamp => timestamp > windowStart)

      if (recentTimestamps.length >= maxEventsPerWindow) {
         timestampsByFingerprint.set(fingerprint, recentTimestamps)
         return false
      }

      if (timestampsByFingerprint.size >= maxFingerprints && !timestampsByFingerprint.has(fingerprint)) {
         timestampsByFingerprint.clear()
      }

      recentTimestamps.push(currentTime)
      timestampsByFingerprint.set(fingerprint, recentTimestamps)
      return true
   }
}
